/**
 * useTimetableGridEditing — ScheduleDraft Timetable 可编辑 Grid 状态与提交层。
 *
 * 本 hook 管理 react-data-grid 的本地行、选择状态和单元格变更提交。
 * Grid 单元格编辑提交通过 editPipeline.executeCommand 发出 EditCommand，
 * Pipeline 负责重算联动字段、构建 ChangeSet 并写入 localState。
 *
 * Grid 的 gridRows 从 timetableModel.rows 单向派生——localState 为权威数据源。
 * gridRows 只是受控 DataGrid 的展示缓存：编辑提交时先同步接收 DataGrid 的
 * newRows，避免输入被旧 rows 还原；Pipeline 成功后由 localDraft 投影覆盖，失败
 * 则回滚到权威 rows。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → components/editor/timetable/timetableRowModel.js (行/payload 投影),
 *             hooks/useScheduleDraftEditPipeline.js (Pipeline 执行),
 *             components/editor/timetable/ScheduleDraftTimetableSection.jsx (装配层)
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Space, Typography } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { writeExcelFriendlyTableToClipboard } from "../../../../../../utils/clipboardTableUtils";
import { UPDATE_FLIGHT_FIELD, UPDATE_FLIGHT_FIELDS } from "../../../lib/editCommand";
import { formatMinutesAsHHMM, normalizeMinutes } from "../../../lib/flightTimingUtils";
import { getRouteById } from "../../../lib/routeUtils";
import { buildDateTimeWithTime, buildEditableRows, buildFlightUpdateValues } from "./timetableRowModel";
import { getCellKey, getCellsInRectangle, getColumnCells, getFlightIdFromRow, isSelectableGridColumn } from "./timetableSelection";

dayjs.extend(utc);

const { Text } = Typography;

const RDG_ROW_NUMBER_COLUMN_KEY = "__rdg-row-number";
const NON_COPYABLE_COLUMN_KEYS = new Set([RDG_ROW_NUMBER_COLUMN_KEY, "actions"]);
const DELETED_CHANGE_STATUS = "DELETED";
export const STA_EDIT_INTENTS = Object.freeze({
    KEEP_STD_ADJUST_BLOCK: "KEEP_STD_ADJUST_BLOCK",
    KEEP_BLOCK_ADJUST_STD: "KEEP_BLOCK_ADJUST_STD",
});
export const STA_EDIT_CONFIRM_TITLE = "确认到达时刻调整方式";
export const STA_EDIT_CONFIRM_CONTENT = "你修改了 STA。请选择系统如何处理起飞时刻与轮档时间的关系。";

/**
 * Grid 列 key → 航班字段名的映射。
 *
 * react-data-grid 的 column.key 使用 camelCase 展示字段名（如 departureAirport），
 * 而后端和 localState 使用 snake_case（如 departure_airport）。此映射确保 Pipeline
 * 收到的字段名与 localState 中的字段一致。
 */
const GRID_KEY_TO_FLIGHT_FIELD = {
    departureAirport: "departure_airport",
    arrivalAirport: "arrival_airport",
    stdTime: "std",
    staTime: "sta",
    flightNumber: "flight_number",
    aircraftReg: "aircraft_reg",
    stcType: "stc_type",
    isFixed: "is_fixed",
    remarks: "remarks",
    flightRouteId: "flight_route_id",
};

function normalizeCellRange(range) {
    if (!range) {
        return null;
    }

    return {
        startRowIdx: Math.min(range.startRowIdx, range.endRowIdx),
        endRowIdx: Math.max(range.startRowIdx, range.endRowIdx),
        startColIdx: Math.min(range.startColIdx, range.endColIdx),
        endColIdx: Math.max(range.startColIdx, range.endColIdx),
    };
}

function isCopyableColumn(column) {
    return Boolean(column?.key) && !NON_COPYABLE_COLUMN_KEYS.has(column.key);
}

function getConstraintStatusText(violations = []) {
    if (!violations.length) {
        return "正常";
    }

    return violations.map((violation) => violation?.message || violation?.type || violation?.constraint_type || violation?.violationType || violation?.code || "约束违规").join("; ");
}

function isDeletedRow(row) {
    return String(row?.rawFlight?.change_status || row?.changeStatus || "").toUpperCase() === DELETED_CHANGE_STATUS;
}

function calculateBlockTimeMinutes(std, sta) {
    if (!std || !sta) {
        return null;
    }

    const stdTime = dayjs.utc(std);
    const staTime = dayjs.utc(sta);
    if (!stdTime.isValid() || !staTime.isValid()) {
        return null;
    }

    const effectiveStaTime = staTime.isBefore(stdTime) ? staTime.add(1, "day") : staTime;
    return effectiveStaTime.diff(stdTime, "minute");
}

function calculateStdFromSta(sta, blockTimeMinutes) {
    const normalizedBlockTime = normalizeMinutes(blockTimeMinutes);
    if (!sta || normalizedBlockTime === null) {
        return null;
    }

    const staTime = dayjs.utc(sta);
    if (!staTime.isValid()) {
        return null;
    }

    return staTime.subtract(normalizedBlockTime, "minute").toISOString();
}

function getRouteTimingForFlight(flight, referenceData = {}) {
    const routeId = flight?.flight_route_id ?? flight?.route_snapshot?.id ?? null;
    const referenceRoute = getRouteById(referenceData, routeId);
    if (referenceRoute) {
        return {
            route: referenceRoute,
            blockTimeMinutes: normalizeMinutes(referenceRoute.block_time_minutes),
            minBlockTimeMinutes: normalizeMinutes(referenceRoute.min_block_time_minutes),
        };
    }

    const routeSnapshot = flight?.route_snapshot || null;
    return {
        route: routeSnapshot,
        blockTimeMinutes: normalizeMinutes(routeSnapshot?.block_time_minutes ?? flight?.flight_route_block_time_minutes),
        minBlockTimeMinutes: normalizeMinutes(routeSnapshot?.min_block_time_minutes),
    };
}

export function confirmStaEditIntentWithModal({ routeBlockTimeMinutes = null } = {}) {
    return new Promise((resolve) => {
        let modalRef = null;
        let settled = false;
        const finish = (intent) => {
            if (settled) {
                return;
            }
            settled = true;
            modalRef?.destroy?.();
            resolve(intent || null);
        };
        const keepBlockDisabled = routeBlockTimeMinutes === null;

        modalRef = Modal.confirm({
            title: STA_EDIT_CONFIRM_TITLE,
            content: (
                <div>
                    <p>{STA_EDIT_CONFIRM_CONTENT}</p>
                    {keepBlockDisabled ? <Text type="warning">当前航路缺少计划轮档时间，无法自动反推 STD。</Text> : null}
                    <Space wrap style={{ marginTop: 16 }}>
                        <Button type="primary" onClick={() => finish(STA_EDIT_INTENTS.KEEP_STD_ADJUST_BLOCK)}>保持 STD，调整轮档</Button>
                        <Button disabled={keepBlockDisabled} onClick={() => finish(STA_EDIT_INTENTS.KEEP_BLOCK_ADJUST_STD)}>保持轮档，调整 STD</Button>
                        <Button onClick={() => finish(null)}>取消</Button>
                    </Space>
                </div>
            ),
            icon: null,
            footer: null,
            keyboard: true,
            maskClosable: false,
            onCancel: () => finish(null),
            afterClose: () => finish(null),
        });
    });
}

function getCopyableCellValue(row, column) {
    if (!row || row._rowType !== "flight" || !column?.key) {
        return "";
    }

    switch (column.key) {
        case "flightRouteId":
            return row.flightRouteFullName || row.flightRouteName || row.flightRouteSummary || row.flightRouteId || "";
        case "blockTime":
            return formatMinutesAsHHMM(row.blockTimeMinutes);
        case "groundTime":
            return formatMinutesAsHHMM(row.groundTimeMinutes);
        case "constraintStatus":
            return getConstraintStatusText(row.constraintViolations);
        case "isFixed":
            return row.isFixed ? "固定" : "可编辑";
        default:
            return row[column.key] ?? "";
    }
}

/**
 * 绑定 Timetable Grid 的编辑、选择与提交行为。
 *
 * @param {Object} params - Grid 编辑参数。
 * @param {string|number} params.draftId - 当前草稿 ID。
 * @param {Array<Object>} params.rows - Timetable ViewModel rows。
 * @param {boolean} params.canUpdateFlight - 当前用户是否可编辑航班。
 * @param {(command: import("../../../lib/editCommand").EditCommand) => Promise<import("../../../lib/editCommand").EditPipelineResult>} params.executeCommand - Pipeline 执行函数。
 * @param {(itemId: string|null) => void} params.setSelectedItemId - Timeline/Sidebar 选中项 setter。
 * @param {Object} [params.referenceData] - 参考数据（routesById 等），供重算规则使用。
 * @returns {Object} 供 TimetableEditableGrid 消费的状态与回调。
 */
export default function useTimetableGridEditing({
    draftId,
    rows,
    canUpdateFlight,
    executeCommand,
    setSelectedItemId,
    selectedCells,
    setSelectedCells,
    hoveredCell,
    setHoveredCell,
    clearSelectedCells,
    referenceData,
    loadReferenceDataForEdit,
    confirmStaEditIntent = confirmStaEditIntentWithModal,
}) {
    const [gridRows, setGridRows] = useState([]);
    const [selectedCellRange, setSelectedCellRange] = useState(null);
    const [activeRouteCellKey, setActiveRouteCellKey] = useState(null);
    const selectedCellRef = useRef(null);
    const selectedCellRangeRef = useRef(null);
    const selectedCellsRef = useRef(selectedCells || new Set());
    const hoveredCellRef = useRef(hoveredCell || null);
    const isSelectingCellRangeRef = useRef(false);
    const cellRangeAnchorRef = useRef(null);
    const selectionColumnsRef = useRef(null);
    const lastCellPositionRef = useRef(null);
    const gridRef = useRef(null);

    // gridRows 从 timetableModel.rows 单向派生，localState 为权威数据源
    useEffect(() => {
        setGridRows(buildEditableRows(rows));
        selectedCellRangeRef.current = null;
        setSelectedCellRange(null);
        setActiveRouteCellKey(null);
    }, [rows]);

    useEffect(() => {
        selectedCellRangeRef.current = selectedCellRange;
    }, [selectedCellRange]);

    useEffect(() => {
        selectedCellsRef.current = selectedCells || new Set();
    }, [selectedCells]);

    useEffect(() => {
        hoveredCellRef.current = hoveredCell || null;
    }, [hoveredCell]);

    const handleSelectRow = useCallback((record) => {
        if (record?._rowType !== "flight" || !record?.rawFlight?.id) {
            return;
        }

        setSelectedItemId(`draft:${draftId}:${record.rawFlight.id}`);
    }, [draftId, setSelectedItemId]);

    const openRouteSearch = useCallback(
        (row) => {
            if (!canUpdateFlight || row?._rowType !== "flight" || !row?.rawFlight?.id || isDeletedRow(row) || !row.departureAirport || !row.arrivalAirport) {
                return;
            }

            handleSelectRow(row);
            setActiveRouteCellKey(row.id);
        },
        [canUpdateFlight, handleSelectRow],
    );

    const closeRouteSearch = useCallback((row) => {
        setActiveRouteCellKey((currentKey) => {
            if (!row?.id || currentKey === row.id) {
                return null;
            }
            return currentKey;
        });
    }, []);

    const handleRouteSelect = useCallback(
        async (row, route) => {
            const routeId = route?.id ?? null;
            if (!canUpdateFlight || !executeCommand || row?._rowType !== "flight" || !row?.rawFlight?.id || isDeletedRow(row) || routeId == null) {
                closeRouteSearch(row);
                return null;
            }

            closeRouteSearch(row);

            if (String(row.rawFlight.flight_route_id ?? "") === String(routeId)) {
                return null;
            }

            try {
                return await executeCommand({
                    type: UPDATE_FLIGHT_FIELD,
                    draftId,
                    flightId: row.rawFlight.id,
                    payload: {
                        flight_route_id: routeId,
                    },
                    meta: {
                        source: "route-search-popover",
                        columnKey: "flightRouteId",
                        referenceData: {
                            routesById: {
                                [routeId]: route,
                            },
                        },
                    },
                });
            } catch (error) {
                console.warn("Failed to update timetable route:", error);
                return null;
            }
        },
        [canUpdateFlight, closeRouteSearch, draftId, executeCommand],
    );

    const updateSelectedCells = useCallback(
        (nextCells) => {
            const normalizedCells = new Set(nextCells || []);
            selectedCellsRef.current = normalizedCells;
            setSelectedCells?.(normalizedCells);
        },
        [setSelectedCells],
    );

    const updateSelectionAnchor = useCallback(
        (cellKey) => {
            hoveredCellRef.current = cellKey || null;
            setHoveredCell?.(cellKey || null);
        },
        [setHoveredCell],
    );

    const findCellPositionByKey = useCallback(
        (cellKey, columns) => {
            if (!cellKey || !Array.isArray(columns)) {
                return null;
            }

            for (let rowIdx = 0; rowIdx < gridRows.length; rowIdx += 1) {
                for (let colIdx = 0; colIdx < columns.length; colIdx += 1) {
                    if (getCellKey(gridRows[rowIdx], columns[colIdx]) === cellKey) {
                        return { rowIdx, colIdx };
                    }
                }
            }

            return null;
        },
        [gridRows],
    );

    const applyCellRangeSelection = useCallback(
        (startPosition, endPosition, columns) => {
            if (!startPosition || !endPosition || !Array.isArray(columns)) {
                return;
            }

            const nextRange = {
                startRowIdx: startPosition.rowIdx,
                startColIdx: startPosition.colIdx,
                endRowIdx: endPosition.rowIdx,
                endColIdx: endPosition.colIdx,
            };
            selectedCellRangeRef.current = nextRange;
            setSelectedCellRange(nextRange);
            updateSelectedCells(getCellsInRectangle(gridRows, columns, startPosition, endPosition));
        },
        [gridRows, updateSelectedCells],
    );

    const updateCellRangeEnd = useCallback(
        (position, columns) => {
            if (!cellRangeAnchorRef.current || position?.rowIdx == null || position?.colIdx == null) {
                return;
            }

            const activeColumns = columns || selectionColumnsRef.current;
            if (!Array.isArray(activeColumns)) {
                return;
            }

            const { rowIdx, colIdx } = position;
            if (rowIdx < 0 || rowIdx >= gridRows.length || colIdx < 0 || colIdx >= activeColumns.length) {
                return;
            }

            lastCellPositionRef.current = { rowIdx, colIdx };
            applyCellRangeSelection(cellRangeAnchorRef.current, { rowIdx, colIdx }, activeColumns);
        },
        [applyCellRangeSelection, gridRows.length],
    );

    const handleCellMouseDown = useCallback(
        (cellContext, event) => {
            const { position, row, column, columns } = cellContext || {};
            if (!position || event?.button !== 0 || position.rowIdx == null || position.colIdx == null || !isSelectableGridColumn(column)) {
                return;
            }

            const cellKey = getCellKey(row, column);
            if (!cellKey) {
                return;
            }

            selectedCellRef.current = cellKey;
            event?.preventDefault?.();

            if (event?.shiftKey) {
                const anchorPosition = findCellPositionByKey(hoveredCellRef.current, columns) || position;
                applyCellRangeSelection(anchorPosition, position, columns);
                if (!hoveredCellRef.current) {
                    updateSelectionAnchor(cellKey);
                }
                return;
            }

            if (event?.metaKey || event?.ctrlKey) {
                const nextCells = new Set(selectedCellsRef.current);
                if (nextCells.has(cellKey)) {
                    nextCells.delete(cellKey);
                } else {
                    nextCells.add(cellKey);
                }
                updateSelectedCells(nextCells);
                updateSelectionAnchor(cellKey);
                selectedCellRangeRef.current = {
                    startRowIdx: position.rowIdx,
                    startColIdx: position.colIdx,
                    endRowIdx: position.rowIdx,
                    endColIdx: position.colIdx,
                };
                setSelectedCellRange(selectedCellRangeRef.current);
                return;
            }

            cellRangeAnchorRef.current = { rowIdx: position.rowIdx, colIdx: position.colIdx };
            selectionColumnsRef.current = columns;
            lastCellPositionRef.current = { rowIdx: position.rowIdx, colIdx: position.colIdx };
            isSelectingCellRangeRef.current = true;
            updateSelectionAnchor(cellKey);
            applyCellRangeSelection(cellRangeAnchorRef.current, cellRangeAnchorRef.current, columns);
        },
        [applyCellRangeSelection, findCellPositionByKey, updateSelectedCells, updateSelectionAnchor],
    );

    const handleCellMouseMove = useCallback(
        (cellContext) => {
            const { position, columns } = cellContext || {};
            if (!isSelectingCellRangeRef.current || !position) {
                return;
            }

            if (lastCellPositionRef.current?.rowIdx === position.rowIdx && lastCellPositionRef.current?.colIdx === position.colIdx) {
                return;
            }

            updateCellRangeEnd(position, columns);
        },
        [updateCellRangeEnd],
    );

    const handleCellMouseUp = useCallback(() => {
        isSelectingCellRangeRef.current = false;
        lastCellPositionRef.current = null;
        selectionColumnsRef.current = null;
    }, []);

    const handleCellNavigation = useCallback(
        (cellContext) => {
            const { position, row, column, columns } = cellContext || {};
            if (!position || position.rowIdx == null || position.colIdx == null || !isSelectableGridColumn(column)) {
                return;
            }

            const cellKey = getCellKey(row, column);
            if (!cellKey) {
                return;
            }

            selectedCellRef.current = cellKey;
            updateSelectionAnchor(cellKey);
            updateSelectedCells(new Set([cellKey]));
            const nextRange = {
                startRowIdx: position.rowIdx,
                startColIdx: position.colIdx,
                endRowIdx: position.rowIdx,
                endColIdx: position.colIdx,
            };
            selectedCellRangeRef.current = nextRange;
            setSelectedCellRange(nextRange);
            selectionColumnsRef.current = columns || null;
            handleSelectRow(row);
        },
        [handleSelectRow, updateSelectedCells, updateSelectionAnchor],
    );

    const handleColumnHeaderClick = useCallback(
        (column, columns, event) => {
            if (!isSelectableGridColumn(column)) {
                return;
            }

            const columnCells = getColumnCells(gridRows, column);
            if (!columnCells.size) {
                return;
            }

            if (event?.metaKey || event?.ctrlKey) {
                const nextCells = new Set(selectedCellsRef.current);
                const shouldRemoveColumn = Array.from(columnCells).every((cellKey) => nextCells.has(cellKey));
                columnCells.forEach((cellKey) => {
                    if (shouldRemoveColumn) {
                        nextCells.delete(cellKey);
                    } else {
                        nextCells.add(cellKey);
                    }
                });
                updateSelectedCells(nextCells);
            } else {
                updateSelectedCells(columnCells);
            }

            const firstCellKey = columnCells.values().next().value || null;
            updateSelectionAnchor(firstCellKey);

            const columnIndex = Array.isArray(columns) ? columns.findIndex((candidate) => candidate?.key === column.key) : -1;
            if (columnIndex >= 0) {
                const flightRowIndexes = gridRows.map((row, rowIdx) => (row?._rowType === "flight" && getFlightIdFromRow(row) != null ? rowIdx : null)).filter((rowIdx) => rowIdx != null);
                const firstRowIdx = flightRowIndexes[0];
                const lastRowIdx = flightRowIndexes[flightRowIndexes.length - 1];
                if (firstRowIdx != null && lastRowIdx != null) {
                    const nextRange = {
                        startRowIdx: firstRowIdx,
                        startColIdx: columnIndex,
                        endRowIdx: lastRowIdx,
                        endColIdx: columnIndex,
                    };
                    selectedCellRangeRef.current = nextRange;
                    setSelectedCellRange(nextRange);
                }
            }
        },
        [gridRows, updateSelectedCells, updateSelectionAnchor],
    );

    const findCellAtPosition = useCallback(
        (clientX, clientY) => {
            const gridElement = gridRef.current?.querySelector?.(".rdg") || gridRef.current;
            if (!gridElement) {
                return null;
            }

            const gridRect = gridElement.getBoundingClientRect();
            if (clientX < gridRect.left || clientX > gridRect.right || clientY < gridRect.top || clientY > gridRect.bottom) {
                return null;
            }

            const element = document.elementFromPoint(clientX, clientY);
            const rowElement = element?.closest?.(".rdg-row[data-rdg-row-idx]");
            const cellElement = element?.closest?.(".rdg-cell");
            if (!rowElement || !cellElement) {
                return null;
            }

            const rowIdx = Number(rowElement.getAttribute("data-rdg-row-idx"));
            const colIdx = Number(cellElement.getAttribute("aria-colindex")) - 1;
            if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) {
                return null;
            }

            return { rowIdx, colIdx };
        },
        [gridRef],
    );

    useEffect(() => {
        const handleGlobalMouseMove = (event) => {
            if (!isSelectingCellRangeRef.current) {
                return;
            }

            const position = findCellAtPosition(event.clientX, event.clientY);
            if (position) {
                handleCellMouseMove({ position });
            }
        };

        document.addEventListener("mousemove", handleGlobalMouseMove, { passive: true });
        document.addEventListener("mouseup", handleCellMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleGlobalMouseMove);
            document.removeEventListener("mouseup", handleCellMouseUp);
        };
    }, [findCellAtPosition, handleCellMouseMove, handleCellMouseUp]);

    const clearCellSelection = useCallback(() => {
        selectedCellRangeRef.current = null;
        setSelectedCellRange(null);
        selectedCellRef.current = null;
        selectedCellsRef.current = new Set();
        cellRangeAnchorRef.current = null;
        selectionColumnsRef.current = null;
        lastCellPositionRef.current = null;
        isSelectingCellRangeRef.current = false;
        clearSelectedCells?.();
    }, [clearSelectedCells]);

    const copySelectedCellRange = useCallback(
        async (columns) => {
            const range = normalizeCellRange(selectedCellRangeRef.current);
            if (!range || !Array.isArray(columns)) {
                return false;
            }

            const copyableColumnIndexes = [];
            for (let colIdx = range.startColIdx; colIdx <= range.endColIdx; colIdx += 1) {
                if (isCopyableColumn(columns[colIdx])) {
                    copyableColumnIndexes.push(colIdx);
                }
            }

            if (!copyableColumnIndexes.length) {
                return false;
            }

            const tableRows = [];
            for (let rowIdx = range.startRowIdx; rowIdx <= range.endRowIdx && rowIdx < gridRows.length; rowIdx += 1) {
                const row = gridRows[rowIdx];
                tableRows.push(copyableColumnIndexes.map((colIdx) => getCopyableCellValue(row, columns[colIdx])));
            }

            if (!tableRows.length) {
                return false;
            }

            try {
                await writeExcelFriendlyTableToClipboard(tableRows);
                return true;
            } catch (error) {
                console.warn("Failed to copy timetable cell range:", error);
                return false;
            }
        },
        [gridRows],
    );

    /**
     * react-data-grid onRowsChange 回调。
     *
     * 改造后的流程：
     * 1. 从编辑行中提取变更字段和新值
     * 2. 构建 EditCommand
     * 3. 先同步更新 gridRows 展示缓存，满足 react-data-grid 受控 rows 契约
     * 4. 通过 executeCommand 发给 Pipeline（validate → recalculate → apply to localState）
     * 5. 成功后 gridRows 由 rows prop 变化自动重建；失败/取消则回滚展示缓存
     *
     * 注意：航路变更通过 RouteSearchPopover 直接进入 Pipeline；这里保留
     * flightRouteId 分支，兼容 DataGrid 行变更路径。
     */
    const handleGridRowsChange = async (newRows, { indexes, column }) => {
        if (!canUpdateFlight || !executeCommand) {
            return { status: "failure" };
        }

        const rowIndex = indexes?.[0];
        if (rowIndex === undefined) {
            return { status: "noop" };
        }

        const editedRow = newRows[rowIndex];
        if (!editedRow || editedRow._rowType !== "flight" || !editedRow.rawFlight?.id || isDeletedRow(editedRow)) {
            return { status: "noop" };
        }

        const flightId = editedRow.rawFlight.id;
        const previousRows = gridRows;
        setGridRows(newRows);

        // 从编辑后的行提取完整更新值
        const updateValues = buildFlightUpdateValues(editedRow);
        const rawFlight = editedRow.rawFlight;

        // 确定哪些字段实际发生了变化（比较编辑行 vs rawFlight）
        let changedPayload = {};
        const columnKey = column?.key;

        if (columnKey && GRID_KEY_TO_FLIGHT_FIELD[columnKey]) {
            // 单字段编辑：精确识别变更字段
            const flightField = GRID_KEY_TO_FLIGHT_FIELD[columnKey];
            changedPayload[flightField] = updateValues[flightField];
        } else if (columnKey === "flightRouteId" || columnKey === "route") {
            // 航路变更的关联字段由 Pipeline 重算规则统一派生
            changedPayload.flight_route_id = updateValues.flight_route_id;
            changedPayload.block_time_minutes = updateValues.block_time_minutes;
            changedPayload.min_block_time_minutes = updateValues.min_block_time_minutes;
            // STA 由重算规则自动处理，不在此处显式设置
        } else {
            // 回退：对比所有字段提取差异
            Object.entries(updateValues).forEach(([field, value]) => {
                const oldValue = rawFlight[field];
                if (value !== oldValue && !(value == null && oldValue == null)) {
                    changedPayload[field] = value;
                }
            });
        }

        if (Object.keys(changedPayload).length === 0) {
            return { status: "noop" };
        }

        // 特殊处理时间字段：Grid 展示值通常是 HH:mm；日期编辑器也可能传入完整 datetime。
        // buildDateTimeWithTime 会保留完整 datetime，或把 HH:mm 合并到原 UTC 日期。
        if ("std" in changedPayload && editedRow.stdTime) {
            changedPayload.std = buildDateTimeWithTime(rawFlight.std, editedRow.stdTime);
        }
        if ("sta" in changedPayload && editedRow.staTime) {
            changedPayload.sta = buildDateTimeWithTime(rawFlight.sta || rawFlight.std, editedRow.staTime);
        }

        let effectiveReferenceData = referenceData || {};
        if (loadReferenceDataForEdit && ("std" in changedPayload || "sta" in changedPayload || "flight_route_id" in changedPayload)) {
            try {
                effectiveReferenceData = await loadReferenceDataForEdit({
                    row: editedRow,
                    payload: changedPayload,
                    referenceData: effectiveReferenceData,
                }) || effectiveReferenceData;
            } catch (error) {
                setGridRows(previousRows);
                return { status: "failure", error };
            }
        }

        if ("sta" in changedPayload) {
            const routeTiming = getRouteTimingForFlight(rawFlight, effectiveReferenceData);
            const intent = await confirmStaEditIntent({
                row: editedRow,
                flight: rawFlight,
                sta: changedPayload.sta,
                routeBlockTimeMinutes: routeTiming.blockTimeMinutes,
                routeMinBlockTimeMinutes: routeTiming.minBlockTimeMinutes,
            });

            if (!intent) {
                setGridRows(previousRows);
                return { status: "canceled" };
            }

            if (intent === STA_EDIT_INTENTS.KEEP_STD_ADJUST_BLOCK) {
                const blockTimeMinutes = calculateBlockTimeMinutes(rawFlight.std, changedPayload.sta);
                if (blockTimeMinutes === null) {
                    setGridRows(previousRows);
                    return { status: "failure", error: new Error("无法根据 STD/STA 计算轮档时间") };
                }
                changedPayload = {
                    sta: changedPayload.sta,
                    block_time_minutes: blockTimeMinutes,
                };
            } else if (intent === STA_EDIT_INTENTS.KEEP_BLOCK_ADJUST_STD) {
                const std = calculateStdFromSta(changedPayload.sta, routeTiming.blockTimeMinutes);
                if (!std) {
                    setGridRows(previousRows);
                    return { status: "failure", error: new Error("当前航路缺少计划轮档时间，无法自动反推 STD") };
                }
                changedPayload = {
                    std,
                    sta: changedPayload.sta,
                    block_time_minutes: routeTiming.blockTimeMinutes,
                };
            }
        }

        const commandType = Object.keys(changedPayload).length === 1
            ? UPDATE_FLIGHT_FIELD
            : UPDATE_FLIGHT_FIELDS;

        try {
            const result = await executeCommand({
                type: commandType,
                draftId,
                flightId,
                payload: changedPayload,
                meta: {
                    source: "grid",
                    columnKey,
                    referenceData: effectiveReferenceData,
                },
            });

            if (result?.status !== "success") {
                setGridRows(previousRows);
                return { status: "failure", result };
            }

            return { status: "success", result };
        } catch (error) {
            setGridRows(previousRows);
            return { status: "failure", error };
        }
    };

    const rowClass = useCallback((row) => {
        const classes = [];
        if (row?._rowType) {
            classes.push(`schedule-draft-rdg-row-${row._rowType}`);
        }
        if (row?._previewRole) {
            classes.push(`schedule-draft-rdg-row-preview-${row._previewRole}`);
        }
        if (row?.rawFlight?._dirty) {
            classes.push("schedule-draft-rdg-row-dirty");
        }
        if (isDeletedRow(row)) {
            classes.push("schedule-draft-rdg-row-deleted");
        }
        if (row?._hasRotationGapAfter) {
            classes.push("schedule-draft-rdg-row-rotation-gap-after");
        }
        return classes.length ? classes.join(" ") : undefined;
    }, []);

    return {
        gridRows,
        selectedCellRange,
        selectedCellRef,
        activeRouteCellKey,
        gridRef,
        handleSelectRow,
        openRouteSearch,
        closeRouteSearch,
        handleRouteSelect,
        handleGridRowsChange,
        handleCellMouseDown,
        handleCellMouseMove,
        handleCellMouseUp,
        handleCellNavigation,
        handleColumnHeaderClick,
        clearCellSelection,
        copySelectedCellRange,
        rowClass,
    };
}
